import { useState } from "react";
import { Ruler, Undo2, Trash2, X, Check, RotateCcw, RotateCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { pathM, vallasFor, fmtM, VALLA_M } from "@/lib/geo";

const Box = ({ children, testid, tone = "sky" }) => (
  <div className={`absolute bottom-16 left-1/2 -translate-x-1/2 z-[600] bg-white/95 backdrop-blur-md border rounded-xl p-3 w-[320px] rise ${tone === "amber" ? "border-amber-400/40" : "border-sky-500/40"}`} data-testid={testid}>
    {children}
  </div>
);

export const MeasurePanel = ({ pts, onUndo, onClear, onClose }) => {
  const m = pathM(pts);
  return (
    <Box testid="measure-panel">
      <div className="flex items-center gap-2 text-xs text-sky-700 font-semibold"><Ruler className="w-4 h-4" /> Regla · pulse puntos en el mapa</div>
      <div className="flex items-end gap-6 mt-2">
        <div><div className="font-mono text-[0.625rem] text-slate-500">DISTANCIA</div><div className="font-display text-2xl font-bold text-slate-900" data-testid="measure-distance">{fmtM(m)}</div></div>
        <div><div className="font-mono text-[0.625rem] text-slate-500">VALLAS ({VALLA_M} m)</div><div className="font-display text-2xl font-bold text-amber-700" data-testid="measure-vallas">{vallasFor(m)}</div></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onUndo} disabled={!pts.length} className="px-2 py-1.5 rounded-md bg-slate-100 text-xs disabled:opacity-40" data-testid="measure-undo-button"><Undo2 className="w-3.5 h-3.5" /></button>
        <button onClick={onClear} disabled={!pts.length} className="px-2 py-1.5 rounded-md bg-slate-100 text-xs disabled:opacity-40" data-testid="measure-clear-button"><Trash2 className="w-3.5 h-3.5" /></button>
        <button onClick={onClose} className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-100 text-xs" data-testid="measure-close-button"><X className="w-3.5 h-3.5" /> Cerrar regla</button>
      </div>
    </Box>
  );
};

const BOX_LABEL = { sign: "objeto(s)", cut_point: "corte(s)", street_cut: "tramo(s)", no_parking: "zona(s)", special_path: "tránsito(s) especial(es)", reversed_oneway: "sentido(s) invertido(s)" };

export const BoxSavePanel = ({ corners, contents, onSave, onCancel }) => {
  const [name, setName] = useState("");
  const count = contents?.inside.length || 0;
  const byKind = contents ? Object.entries(contents.inside.reduce((m, e) => ({ ...m, [e.kind]: (m[e.kind] || 0) + 1 }), {})) : [];
  return (
    <Box testid="box-template-panel" tone="amber">
      <div className="text-xs text-amber-700 font-semibold">Plantilla de recuadro</div>
      <div className="text-xs text-slate-500 mt-1" data-testid="box-template-summary">
        {corners.length < 2 ? (corners.length ? "Pulse la esquina opuesta del recuadro." : "Pulse una esquina del recuadro con lo que quiera guardar.")
          : count ? `Se guardarán ${byKind.map(([k, n]) => `${n} ${BOX_LABEL[k] || k}`).join(", ")}.` : "No hay cortes, tramos, zonas ni objetos enteros dentro del recuadro."}
      </div>
      {contents && (contents.partial > 0 || contents.posts > 0) && (
        <div className="text-[0.625rem] text-slate-500 mt-1 leading-snug">
          {contents.partial > 0 && <div>{contents.partial} elemento(s) cortados por el borde no se incluyen: amplíe el recuadro.</div>}
          {contents.posts > 0 && <div>{contents.posts} puesto(s) de personal no se guardan: dependen del briefing de cada evento.</div>}
          <div>El recorrido tampoco se incluye.</div>
        </div>
      )}
      {corners.length === 2 && (
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (p. ej. Cruce en T con desvío)" className="h-8 text-xs mt-2" data-testid="box-template-name-input" />
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={() => onSave(name.trim())} disabled={corners.length < 2 || !count || !name.trim()} data-testid="box-template-save-button"
          className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-400 text-slate-950 text-xs font-semibold disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Guardar plantilla</button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-md bg-slate-100 text-xs" data-testid="box-template-cancel-button">Cancelar</button>
      </div>
    </Box>
  );
};

export const GroupPlacePanel = ({ tpl, placed, rot, setRot, onDone, onCancel }) => (
  <Box testid="group-place-panel" tone="amber">
    <div className="text-xs text-amber-700 font-semibold">{tpl.name}</div>
    <div className="text-xs text-slate-500 mt-1">{placed ? "Gire el conjunto para ajustarlo a la calle. Puede pulsar otro punto para moverlo." : "Pulse en el mapa el cruce donde colocar la plantilla."}</div>
    {placed && (
      <div className="flex items-center gap-2 mt-3" data-testid="group-rotation">
        <button onClick={() => setRot((rot + 345) % 360)} className="p-1.5 rounded-md bg-slate-50 hover:bg-slate-100" data-testid="group-rotate-left"><RotateCcw className="w-4 h-4" /></button>
        <Slider value={[rot]} min={0} max={359} step={5} onValueChange={([v]) => setRot(v)} className="flex-1" data-testid="group-rotation-slider" />
        <button onClick={() => setRot((rot + 15) % 360)} className="p-1.5 rounded-md bg-slate-50 hover:bg-slate-100" data-testid="group-rotate-right"><RotateCw className="w-4 h-4" /></button>
        <span className="font-mono text-xs text-slate-700 w-10 text-right">{rot}°</span>
      </div>
    )}
    <div className="flex gap-2 mt-3">
      <button onClick={onDone} disabled={!placed} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500 text-slate-950 text-xs font-semibold disabled:opacity-40" data-testid="group-place-done-button"><Check className="w-3.5 h-3.5" /> Hecho</button>
      <button onClick={onCancel} className="px-3 py-1.5 rounded-md bg-slate-100 text-xs" data-testid="group-place-cancel-button">Cancelar</button>
    </div>
  </Box>
);
